import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  Building2,
  MapPin,
  Briefcase,
  Wallet,
  Clock,
  Route,
  Check,
  CheckCircle2,
  HelpCircle,
  ChevronDown,
} from 'lucide-react';
import { JobDescription } from './JobDescription';

const GLANCE_ICONS = {
  building: Building2,
  pin: MapPin,
  briefcase: Briefcase,
  wallet: Wallet,
  clock: Clock,
  route: Route,
};

const Spinner = () => <span className="slf-spinner" aria-hidden="true" />;

// Confirms "Not right now" before it fires — a mis-tap here is a one-way door
// (DeclinedStage has no way back to this screen), so guard it explicitly.
function ConfirmDeclineDialog({ onCancel, onConfirm, pending }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Portaled to <body>: `.slf-stage-in` carries a fill-mode `both` entrance
  // animation, which keeps inducing a containing block for fixed-position
  // descendants even after it finishes — a `position: fixed` overlay nested
  // inside it never actually reaches the viewport. Escaping via a portal
  // sidesteps that (and any future ancestor transform/contain) for good.
  // Re-wrapped in `.slf-root` because the --slf-* design tokens the dialog
  // uses are only defined on that class — outside it `var(--slf-surface)`
  // etc. resolve to nothing. The wrapper has no visible content of its own
  // (its only child is `position: fixed`), so it adds no layout footprint.
  return createPortal(
    <div className="slf-root">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="slf-confirm-overlay"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) onCancel();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        tabIndex={-1}
      >
        <div
          className="slf-confirm-card"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="slf-confirm-title"
          aria-describedby="slf-confirm-desc"
        >
          <h3 id="slf-confirm-title">Not interested in this role?</h3>
          <p id="slf-confirm-desc">
            We won&apos;t follow up about this one again — and there&apos;s no
            way back to this screen afterwards. If this was a mis-tap, you can
            keep it open instead.
          </p>
          <div className="slf-actions">
            <button
              ref={cancelRef}
              className="slf-btn slf-btn-primary"
              type="button"
              onClick={onCancel}
            >
              Actually, I&apos;m interested
            </button>
            <button
              className="slf-btn slf-btn-secondary"
              type="button"
              onClick={onConfirm}
              disabled={pending}
            >
              {pending ? 'One moment…' : 'No, not interested'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

ConfirmDeclineDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  pending: PropTypes.bool.isRequired,
};

// Fixed answer bar. Mirrors whichever in-page answer block is live and slides
// away (`data-away`) while those buttons are on screen, so there are never two
// copies of the same action visible at once.
function DecisionBar({
  acknowledged,
  away,
  onYes,
  onNo,
  onContinue,
  yesPending,
  noPending,
}) {
  return (
    <div
      className="slf-decide"
      role="region"
      aria-label="Your answer"
      data-away={away ? 'true' : 'false'}
    >
      <div className="slf-decide-in">
        <p className="slf-decide-txt">
          {acknowledged ? (
            <>
              <b>You told us you&apos;re interested.</b>A few quick details are
              next.
            </>
          ) : (
            <>
              <b>Open to hearing more?</b>Saying yes only means we can call you.
            </>
          )}
        </p>
        {acknowledged ? (
          <div className="slf-decide-btns">
            <button
              className="slf-btn slf-btn-primary"
              type="button"
              onClick={onContinue}
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="slf-decide-btns">
            <button
              className="slf-btn slf-btn-primary"
              type="button"
              onClick={onYes}
              disabled={yesPending || noPending}
            >
              <Check aria-hidden="true" />
              Yes, I&apos;m interested
            </button>
            <button
              className="slf-btn slf-btn-secondary"
              type="button"
              onClick={onNo}
              disabled={yesPending || noPending}
            >
              Not right now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

DecisionBar.propTypes = {
  acknowledged: PropTypes.bool.isRequired,
  away: PropTypes.bool.isRequired,
  onYes: PropTypes.func.isRequired,
  onNo: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  yesPending: PropTypes.bool.isRequired,
  noPending: PropTypes.bool.isRequired,
};

export function IntroStage({
  fields,
  acknowledged,
  ackLabel,
  onYes,
  onNo,
  onContinue,
  yesPending,
  noPending,
  floatingBar,
}) {
  const f = fields;
  const company = f.companyName || 'the company';

  // "No" is a one-way door once it reaches the server, so both "No" entry
  // points (the in-page ask + the sticky decision bar) go through this
  // confirmation instead of calling onNo directly.
  const [confirmDecline, setConfirmDecline] = useState(false);
  const requestDecline = () => setConfirmDecline(true);

  // The decision bar hides while the in-page answer block is visible.
  const answerRef = useRef(null);
  const [answerInView, setAnswerInView] = useState(false);
  useEffect(() => {
    const el = answerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setAnswerInView(entry.isIntersecting),
      { rootMargin: '0px 0px -12px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <section className="slf-stage-in" aria-labelledby="slf-introHead">
        <p className="slf-eyebrow">A note from AHEDGE</p>
        <h1 id="slf-introHead" style={{ marginTop: 10 }}>
          {f.jobTitle}
        </h1>
        {f.subhead ? <p className="slf-subhead">{f.subhead}</p> : null}

        <div style={{ marginTop: 18 }}>
          <p className="slf-read">
            Hi {f.firstName} — we&apos;re <strong>AHEDGE</strong>, a hiring
            partner working with {company}. Your background lined up closely
            with a role they&apos;re hiring for, so we&apos;re checking with you
            directly before taking it any further.
          </p>
          <p className="slf-read">
            Here&apos;s the role in full. No sign-up, and nothing has been
            shared with anyone yet.
          </p>
        </div>

        {f.glance.length ? (
          <div className="slf-facts" style={{ marginTop: 18 }}>
            {f.glance.map((row) => {
              const Icon = GLANCE_ICONS[row.iconKey] || Building2;
              return (
                <div className="slf-fact" key={row.label}>
                  <Icon className="slf-fact-ic" aria-hidden="true" />
                  <div>
                    <span className="slf-fact-k">{row.label}</span>
                    <span
                      className={`slf-fact-v${row.mono ? ' slf-fact-pay' : ''}`}
                    >
                      {row.value}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <JobDescription md={f.jd} />

        <div id="slf-answer" ref={answerRef} style={{ marginTop: 28 }}>
          {acknowledged ? (
            <div>
              <div className="slf-ack-note">
                <CheckCircle2 className="slf-ic" aria-hidden="true" />
                <div>
                  <strong>You told us you&apos;re interested{ackLabel}.</strong>
                  We&apos;re not going to ask you again.
                </div>
              </div>
              <div className="slf-actions" style={{ marginTop: 14 }}>
                <button
                  className="slf-btn slf-btn-primary"
                  type="button"
                  onClick={onContinue}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <div className="slf-ask">
              <h2>Are you open to hearing more?</h2>
              <p className="slf-read" style={{ marginTop: 6, fontSize: 14 }}>
                Saying yes only means we can call you. Your details stay with
                AHEDGE until you tell us to go ahead.
              </p>
              <div className="slf-actions" style={{ marginTop: 16 }}>
                <button
                  className="slf-btn slf-btn-primary"
                  type="button"
                  onClick={onYes}
                  disabled={yesPending || noPending}
                >
                  {yesPending ? <Spinner /> : <Check aria-hidden="true" />}
                  {yesPending ? 'One moment…' : "Yes, I'm interested"}
                </button>
                <button
                  className="slf-btn slf-btn-secondary"
                  type="button"
                  onClick={requestDecline}
                  disabled={yesPending || noPending}
                >
                  {noPending ? 'One moment…' : 'Not right now'}
                </button>
              </div>
              <p className="slf-note" style={{ marginTop: 12 }}>
                Around a minute of questions if you say yes.
              </p>
            </div>
          )}
        </div>

        {confirmDecline && (
          <ConfirmDeclineDialog
            onCancel={() => setConfirmDecline(false)}
            onConfirm={onNo}
            pending={noPending}
          />
        )}

        <details className="slf-disclose" style={{ marginTop: 14 }}>
          <summary>
            <HelpCircle aria-hidden="true" />
            <span style={{ flex: 1 }}>How did you get my details?</span>
            <ChevronDown className="slf-caret" aria-hidden="true" />
          </summary>
          <div className="slf-disclose-body">
            <p className="slf-read" style={{ fontSize: 13.5 }}>
              Your profile reached us through a public professional profile or a
              referral into our talent network. AHEDGE is a licensed recruitment
              partner — we don&apos;t sell or publish candidate data, and we
              haven&apos;t contacted your current employer. Ask us to delete
              everything we hold on you at any time and we&apos;ll confirm
              within 48 hours.
            </p>
          </div>
        </details>
      </section>

      {/* This bar is `position: fixed; left: 0; right: 0` — correct on the
          magic-link page (no sidebar, owns the full viewport width), but it
          would cut across the app sidebar when this view is opened from the
          authenticated "My Applications" list instead. Suppressed there;
          the in-page Yes/No buttons above cover the same action. */}
      {floatingBar && (
        <DecisionBar
          acknowledged={acknowledged}
          away={answerInView}
          onYes={onYes}
          onNo={requestDecline}
          onContinue={onContinue}
          yesPending={yesPending}
          noPending={noPending}
        />
      )}
    </>
  );
}

IntroStage.propTypes = {
  fields: PropTypes.shape({
    firstName: PropTypes.string,
    jobTitle: PropTypes.string,
    companyName: PropTypes.string,
    subhead: PropTypes.string,
    glance: PropTypes.arrayOf(PropTypes.shape({})),
    jd: PropTypes.string,
  }).isRequired,
  acknowledged: PropTypes.bool,
  ackLabel: PropTypes.string,
  onYes: PropTypes.func.isRequired,
  onNo: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  yesPending: PropTypes.bool,
  noPending: PropTypes.bool,
  floatingBar: PropTypes.bool,
};

IntroStage.defaultProps = {
  acknowledged: false,
  ackLabel: '',
  yesPending: false,
  noPending: false,
  floatingBar: true,
};

export default IntroStage;
